/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton, TextField, Typography } from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import constants from '../../constants';
import {
  CustomDialog,
  DialogHeader,
  DialogContent,
  DialogActions
} from '../../components/common/Dialog';
import './ContentMetadata.style.scss';
import { formatTaxonomyText, convertToKebabCase } from '../../utils/utils';

const ContentMetadataEditBox = props => {
  const {
    open,
    closeEditBox,
    handleAddTaxonomy,
    handleEditTaxonomy,
    isAddNewBox,
    currentValue,
    currentLabel,
    selectedTaxonomyType,
    taxonomies
  } = props;

  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [textBoxValue, setTextBoxValue] = useState(
    isAddNewBox ? '' : currentLabel || ''
  );
  const [subsegmentValues, setSubsegmentValues] = React.useState({});
  const [newSubsegments, setNewSubsegments] = useState([]);
  const [segmentError, setSegmentError] = useState('');
  const [taxonomyError, setTaxonomyError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subsegmentToDelete, setSubsegmentToDelete] = useState(null);
  const [subsegmentFocus, setSubsegmentFocus] = useState({});
  const [mainFieldFocus, setMainFieldFocus] = useState(false);
  const [subsegmentEditError, setSubsegmentEditError] = useState({});
  const validateSubsegmentForDuplicates = (currentKey, value, values) => {
    // Skip validation for empty values
    if (!value || !value.trim()) return null;

    const normalizedValue = value.trim().toLowerCase();
    const isNewField = currentKey.startsWith('new-');

    // For new fields, first check kebab-case duplicates with existing subsegments from API
    // This has the highest priority, so we check it first
    if (isNewField && !isAddNewBox && taxonomies?.length) {
      const currentSegmentSubsegments = [];

      // In edit mode, check against subsegments of the current segment being edited
      const currentTaxonomy = taxonomies.find(
        taxonomy => taxonomy.value === currentValue
      );
      if (currentTaxonomy?.subsegment?.length) {
        currentTaxonomy.subsegment.forEach(sub => {
          if (sub.value && sub.label) {
            currentSegmentSubsegments.push({
              value: sub.value.toLowerCase().trim(),
              label: sub.label.toLowerCase().trim()
            });
          }
        });
      }

      // For new fields, check the kebab-case version against existing values
      const kebabCaseValue = convertToKebabCase(value);

      // First check for duplicate keys (higher priority)
      const isDuplicateKey = currentSegmentSubsegments.some(sub => {
        return sub.value === normalizedValue || sub.value === kebabCaseValue;
      });

      // Then check for duplicate labels
      const isDuplicateLabel = currentSegmentSubsegments.some(sub => {
        return sub.label === normalizedValue;
      });

      if (isDuplicateKey || isDuplicateLabel) {
        return constants.SUB_SEGMENT_DUPLICATE_LABEL_ERROR;
      }
    }

    // Check for duplicate labels within current form (for ALL fields, not just new ones)
    const duplicateLabels = Object.entries(values).filter(
      ([key, val]) =>
        key !== currentKey &&
        val &&
        val.trim().toLowerCase() === normalizedValue
    );

    if (duplicateLabels.length > 0) {
      // Return error for any field that has duplicates in the form
      return constants.SUB_SEGMENT_DUPLICATE_LABEL_ERROR;
    }

    return null;
  };

  // Get original subsegments for required field validation
  const getOriginalSubsegments = () => {
    if (isAddNewBox || !taxonomies?.length || !currentValue) return [];
    const originalTaxonomy = taxonomies.find(
      taxonomy => taxonomy.value === currentValue
    );
    return originalTaxonomy?.subsegment || [];
  };
  // Helper function to validate all subsegments and set save button state
  const validateAllSubsegments = (updatedValues, currentKey) => {
    const errorObj = { ...subsegmentEditError };
    const originalSubsegments = getOriginalSubsegments();

    // Clear current key error initially
    if (currentKey) {
      delete errorObj[currentKey];
    }

    // First, remove errors for any fields that no longer exist
    Object.keys(errorObj).forEach(key => {
      // If the field doesn't exist in updatedValues and is not a required field, remove its error
      if (
        !Object.hasOwn(updatedValues, key) &&
        !originalSubsegments.some(sub => sub.value === key)
      ) {
        delete errorObj[key];
      }
    });

    // Then clear all duplicate errors to start fresh
    Object.keys(errorObj).forEach(key => {
      if (errorObj[key] === constants.SUB_SEGMENT_DUPLICATE_LABEL_ERROR) {
        delete errorObj[key];
      }
    });

    // Single iteration through all fields for validation
    Object.entries(updatedValues).forEach(([key, value]) => {
      const trimmedValue = value?.trim() || '';
      const isRequired = originalSubsegments.some(sub => sub.value === key);

      // Handle empty required fields
      if (!trimmedValue && isRequired) {
        errorObj[key] = constants.SUB_SEGMENT_EMPTY_ERROR;
        return;
      }

      if (!trimmedValue) return;

      // Content validation (special characters and length) - ALWAYS validate on change
      const specialCharRegex = /^[a-zA-Z0-9 -]*$/;
      if (!specialCharRegex.test(trimmedValue)) {
        errorObj[key] = constants.SPECIAL_CHARACTERS_ERROR;
        return;
      }

      if (trimmedValue.length > 150) {
        errorObj[key] = constants.MAX_LENGTH_ERROR;
        return;
      }

      // Duplicate validation (for ALL fields now)
      if (!errorObj[key]) {
        const duplicateError = validateSubsegmentForDuplicates(
          key,
          value,
          updatedValues
        );

        // FIXED: For edit mode, always show API duplicate errors for new fields
        // For form-level duplicates, only show on current field being edited
        if (duplicateError) {
          const isApiDuplicate =
            duplicateError === constants.SUB_SEGMENT_DUPLICATE_LABEL_ERROR;

          // Show API duplicates always, but form duplicates only on current field
          if (isApiDuplicate || !currentKey || key === currentKey) {
            errorObj[key] = duplicateError;
          }
        }
      }
    });

    // Second pass: Find and mark duplicate fields - with kebab-case checking for new fields
    const duplicateGroups = {};
    const kebabCaseGroups = {}; // Track kebab-case values for new fields

    Object.entries(updatedValues).forEach(([key, value]) => {
      if (value && value.trim()) {
        const normalizedValue = value.trim().toLowerCase();

        // Regular value tracking
        if (!duplicateGroups[normalizedValue]) {
          duplicateGroups[normalizedValue] = [];
        }
        duplicateGroups[normalizedValue].push(key);

        // For new fields, also track kebab-case value
        if (key.startsWith('new-')) {
          const kebabCaseValue = convertToKebabCase(normalizedValue);
          if (!kebabCaseGroups[kebabCaseValue]) {
            kebabCaseGroups[kebabCaseValue] = [];
          }
          kebabCaseGroups[kebabCaseValue].push(key);
        }
      }
    });

    // FIXED: Check if there are any duplicates, regardless of which field was changed
    let hasDuplicates = false;

    // eslint-disable-next-line no-unused-vars
    Object.entries(duplicateGroups).forEach(([normalizedValue, keys]) => {
      if (keys.length > 1) {
        // Found duplicates - set the flag
        hasDuplicates = true;

        // Multiple fields have the same value
        let targetKey;
        if (currentKey && keys.includes(currentKey)) {
          // Show error on the field being edited
          targetKey = currentKey;
        } else if (!currentKey) {
          // eslint-disable-next-line prefer-destructuring
          targetKey = keys[0];
        } else {
          // eslint-disable-next-line prefer-destructuring
          targetKey = keys[0];
        }

        if (targetKey && !errorObj[targetKey]) {
          // Regular duplicate labels
          errorObj[targetKey] = constants.SUB_SEGMENT_DUPLICATE_LABEL_ERROR;
        }
      }
    });

    // eslint-disable-next-line no-unused-vars
    Object.entries(kebabCaseGroups).forEach(([kebabValue, newFieldKeys]) => {
      if (newFieldKeys.length > 1) {
        // Found duplicates - set the flag
        hasDuplicates = true;

        // Multiple new fields would generate the same kebab-case value
        let targetKey;

        if (currentKey && newFieldKeys.includes(currentKey)) {
          // Show error on the field being edited
          targetKey = currentKey;
        } else if (!currentKey) {
          // eslint-disable-next-line prefer-destructuring
          targetKey = newFieldKeys[0];
        } else {
          // eslint-disable-next-line prefer-destructuring
          targetKey = newFieldKeys[0];
        }

        if (targetKey && !errorObj[targetKey]) {
          // Use DUPLICATE_LABEL_ERROR for kebab-case duplicates
          errorObj[targetKey] = constants.SUB_SEGMENT_DUPLICATE_LABEL_ERROR;
        }
      }
    });

    // Update error state and save button status
    setSubsegmentEditError(errorObj);

    const hasMainField = textBoxValue.trim().length > 0;
    const hasAnyError = Object.keys(errorObj).length > 0 || hasDuplicates; // FIXED: Consider duplicate flag
    const hasEmptyRequired =
      !isAddNewBox &&
      originalSubsegments.some(sub => !updatedValues[sub.value]?.trim());

    // Also check for main segment field errors
    const hasMainSegmentError = segmentError && segmentError.trim().length > 0;

    setIsSaveEnabled(
      hasMainField && !hasAnyError && !hasEmptyRequired && !hasMainSegmentError
    );
    return errorObj;
  };

  const clearDuplicateErrors = (oldValue, currentKey) => {
    if (!oldValue || !oldValue.trim()) return;
    setSubsegmentEditError(prev => {
      const updated = { ...prev };
      // Only clear duplicate errors on the current field, preserve other errors
      if (updated[currentKey] === constants.SUB_SEGMENT_DUPLICATE_LABEL_ERROR) {
        delete updated[currentKey];
      }
      const normalizedOldValue = oldValue.trim().toLowerCase();
      const isCurrentKeyNewlyAdded = currentKey.startsWith('new-'); // Clear errors in these cases:
      // 1. In add mode: clear for all fields with matching old value
      // 2. In edit mode: clear for newly added fields with matching old value if current field is also newly added
      Object.entries(subsegmentValues).forEach(([key, val]) => {
        if (
          key !== currentKey &&
          val &&
          val.trim().toLowerCase() === normalizedOldValue &&
          updated[key]?.includes(constants.SUB_SEGMENT_DUPLICATE_LABEL_ERROR) &&
          (isAddNewBox || (isCurrentKeyNewlyAdded && key.startsWith('new-')))
        ) {
          // In add mode, always clear matching errors
          // In edit mode, only clear errors between newly added fields
          delete updated[key];
        }
      });
      return updated;
    });
  };
  const handleSubsegmentChange = (event, subsegmentKey) => {
    const { value } = event.target;

    if (value && /\s{2,}/.test(value)) {
      // Found 2 or more consecutive whitespace characters
      return;
    }
    // Capture the current value before it changes (to help with clearing errors)
    const oldValue = subsegmentValues[subsegmentKey];
    const isNewlyAdded = subsegmentKey.startsWith('new-');

    setSubsegmentValues(prev => {
      // Step 1: Create updated subsegments with the new value
      const updatedSubsegments = {
        ...prev,
        [subsegmentKey]: value
      };

      // Step 2: Add missing original subsegments in edit mode
      if (!isAddNewBox && taxonomies?.length && currentValue) {
        const originalSubsegments = taxonomies
          .filter(taxonomy => taxonomy.value === currentValue)
          .flatMap(taxonomy => taxonomy.subsegment || []);

        originalSubsegments.forEach(sub => {
          if (
            !Object.prototype.hasOwnProperty.call(updatedSubsegments, sub.value)
          ) {
            updatedSubsegments[sub.value] = sub.label;
          }
        });
      } // Step 3: Run comprehensive validation (includes special char, length, duplicates, and empty validation)
      validateAllSubsegments(updatedSubsegments, subsegmentKey);

      return updatedSubsegments;
    });

    // Step 6: Clear error messages when the field value changes
    // In add mode: clear for all related fields
    // In edit mode: only clear for newly added fields or the current field
    if (oldValue !== value && (isAddNewBox || isNewlyAdded)) {
      clearDuplicateErrors(oldValue, subsegmentKey);
    } else if (oldValue !== value) {
      // In edit mode for existing fields, only clear duplicate errors, preserve other errors
      setSubsegmentEditError(prev => {
        const updated = { ...prev };
        if (
          updated[subsegmentKey] === constants.SUB_SEGMENT_DUPLICATE_LABEL_ERROR
        ) {
          delete updated[subsegmentKey];
        }
        return updated;
      });
    }
  };
  // Handlers for focus/blur on subsegment fields
  const handleSubsegmentFocus = subsegmentKey => {
    // Only clear duplicate errors on focus, keep other validation errors visible
    setSubsegmentEditError(prev => {
      const updated = { ...prev };
      // Only clear duplicate errors, keep special char and length errors
      if (
        updated[subsegmentKey] === constants.SUB_SEGMENT_DUPLICATE_LABEL_ERROR
      ) {
        delete updated[subsegmentKey];
      }
      return updated;
    });
  };

  const handleSubsegmentBlur = subsegmentKey => {
    // On blur, run full validation to show all errors
    const value = subsegmentValues[subsegmentKey];

    if (value !== undefined) {
      // Run comprehensive validation on blur
      validateAllSubsegments(subsegmentValues, subsegmentKey);
    }
  }; // Helper function to clear duplicate errors when a field value changes

  const resetState = () => {
    setIsSaveEnabled(false);
    setTextBoxValue('');
    setSubsegmentValues({});
    setNewSubsegments([]);
    setSegmentError('');
    setSubsegmentEditError({}); // Clear all subsegment errors on dialog close
    setTaxonomyError(''); // Also clear taxonomy error for completeness
  };

  // reset states on unmounting
  useEffect(() => {
    return resetState;
  }, []);

  useEffect(() => {
    if (isAddNewBox) {
      setTextBoxValue('');
    } else {
      setTextBoxValue(currentLabel || '');
    }
    if (
      !isAddNewBox &&
      taxonomies &&
      taxonomies.length > 0 &&
      currentValue !== ''
    ) {
      // Use segment value (not label) to find the correct taxonomy
      const originalTaxonomy = taxonomies.find(
        taxonomy => taxonomy.value === currentValue
      );
      const originalSubsegments =
        originalTaxonomy && originalTaxonomy.subsegment
          ? originalTaxonomy.subsegment
          : [];
      const subsegmentObj = {};
      originalSubsegments.forEach(sub => {
        subsegmentObj[sub.value] = sub.label;
      });
      setSubsegmentValues(subsegmentObj);
      setNewSubsegments([]);
    }
  }, [currentValue, currentLabel, isAddNewBox, taxonomies]);

  const handleTextBoxValueChange = event => {
    const { value } = event.target;

    if (value && /\s{2,}/.test(value)) {
      // Prevents 2 or more consecutive whitespace characters
      return;
    }
    const trimmedValue = value.trim();
    setTextBoxValue(value);

    // Special character validation: only allow letters, numbers, space, dash, or underscore
    const specialCharRegex = /^[a-zA-Z0-9 -]*$/;
    let error = '';
    if (!specialCharRegex.test(trimmedValue)) {
      error = constants.SPECIAL_CHARACTERS_ERROR;
    } else if (trimmedValue.length === 0) {
      error =
        selectedTaxonomyType === 'segment'
          ? constants.SEGMENT_ERROR
          : `${selectedTaxonomyType.charAt(0).toUpperCase() + selectedTaxonomyType.slice(1)} name cannot be empty`;
    } else if (trimmedValue.length > 150) {
      error = constants.MAX_LENGTH_ERROR;
    }

    if (selectedTaxonomyType === 'segment') {
      setSegmentError(error);
      setTaxonomyError('');
    } else {
      setTaxonomyError(error);
      setSegmentError('');
    }

    // Only enable save if there are no errors and value is not empty
    const hasSubsegmentError = Object.values(subsegmentEditError).some(Boolean);
    setIsSaveEnabled(!error && !hasSubsegmentError && trimmedValue.length > 0);
  };

  /* this functions close this box and reset states */
  const cancel = () => {
    closeEditBox();
    resetState();
  };
  const handleSave = async () => {
    // Prepare subsegments array from subsegmentValues (ignore empty values)
    let subsegments;
    if (isAddNewBox) {
      subsegments = Object.entries(subsegmentValues)
        .map(([key, label]) => {
          if (label && label.trim()) {
            const trimmedLabel = label.trim();
            if (key.startsWith('new-')) {
              // For newly added fields, create the value from the label
              return {
                label: trimmedLabel,
                value: convertToKebabCase(trimmedLabel)
              };
            }
            // For existing fields in add mode (which shouldn't happen, but just in case)
            return { label: trimmedLabel };
          }
          return null;
        })
        .filter(Boolean);
      await handleAddTaxonomy(textBoxValue, subsegments);
    } else {
      // For edit mode
      subsegments = Object.entries(subsegmentValues)
        .map(([value, label]) => {
          if (label && label.trim()) {
            const trimmedLabel = label.trim();
            return {
              label: trimmedLabel,
              // For newly added fields in edit mode, create value from label
              // For existing fields, preserve the original value
              value: value.startsWith('new-')
                ? convertToKebabCase(trimmedLabel)
                : value
            };
          }
          return null;
        })
        .filter(Boolean);
      await handleEditTaxonomy(textBoxValue, subsegments);
    }
    cancel();
  };
  const handleRemoveSubsegment = subsegmentKey => {
    // First clean up any errors for this subsegment
    setSubsegmentEditError(prev => {
      const updated = { ...prev };
      delete updated[subsegmentKey]; // Remove any errors for this key
      return updated;
    });

    setSubsegmentValues(prev => {
      const updatedSubsegments = { ...prev };
      delete updatedSubsegments[subsegmentKey]; // Remove the selected subsegment

      // Re-validate after removal
      validateAllSubsegments(updatedSubsegments, null);

      return updatedSubsegments;
    });
  };
  const handleAddMoreSubsegment = () => {
    let newKey = '';
    let idx = 0;
    while (true) {
      const candidate = `new-${idx}`;
      if (!Object.prototype.hasOwnProperty.call(subsegmentValues, candidate)) {
        newKey = candidate;
        break;
      }
      idx += 1;
    }
    setNewSubsegments(prev => [...prev, newKey]);
    setSubsegmentValues(prev => ({ ...prev, [newKey]: '' }));
    setSubsegmentFocus(prev => ({ ...prev, [newKey]: false }));

    // Clear any potentially duplicate error messages from other fields when adding a new field
    setSubsegmentEditError(prev => {
      // Only keep errors that aren't related to duplicates
      const updatedErrors = {};
      Object.entries(prev).forEach(([key, errorMessage]) => {
        if (
          !errorMessage.includes(constants.SUB_SEGMENT_DUPLICATE_LABEL_ERROR)
        ) {
          updatedErrors[key] = errorMessage;
        }
      });
      return updatedErrors;
    });
  };
  const handleRemoveNewSubsegment = key => {
    // First clean up any errors for this subsegment
    setSubsegmentEditError(prev => {
      const updated = { ...prev };
      delete updated[key]; // Remove any errors for this key
      return updated;
    });

    setNewSubsegments(prev => prev.filter(k => k !== key));
    setSubsegmentValues(prev => {
      const updated = { ...prev };
      delete updated[key];

      // Re-validate after removal
      validateAllSubsegments(updated, null);

      return updated;
    });
  };

  const handleDeleteSubsegmentClick = key => {
    setSubsegmentToDelete(key);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteSubsegment = () => {
    if (subsegmentToDelete) {
      if (newSubsegments.includes(subsegmentToDelete)) {
        handleRemoveNewSubsegment(subsegmentToDelete);
      } else {
        handleRemoveSubsegment(subsegmentToDelete);
      }
    }
    setDeleteDialogOpen(false);
    setSubsegmentToDelete(null);
  };

  const handleCancelDeleteSubsegment = () => {
    setDeleteDialogOpen(false);
    setSubsegmentToDelete(null);
  };

  // Helper to get subsegment label for delete dialog (avoids nested ternaries)
  const getDeleteSubsegmentText = () => {
    if (!subsegmentToDelete) return '';
    // 1. Check subsegmentValues for a label
    const label = subsegmentValues[subsegmentToDelete];
    if (label && label.trim()) return ' ' + label;
    // 2. Search all taxonomies for a subsegment with this value
    if (taxonomies && taxonomies.length > 0) {
      const found = taxonomies
        .flatMap(taxonomy => taxonomy.subsegment || [])
        .find(sub => sub.value === subsegmentToDelete);
      if (found && found.label) return ' ' + found.label;
    }
    return '';
  };

  const dialogTitlePrefix = isAddNewBox ? 'Add New' : 'Edit';

  // Helper function to check for subsegment duplicates
  const checkForDuplicateValues = values => {
    // Check using filter method
    const hasDuplicates =
      values.filter((v, i, arr) => arr.indexOf(v) !== i).length > 0;

    // Check using Set comparison (alternative method)
    const hasDuplicatesSet = values.length !== new Set(values).size;

    return hasDuplicates || hasDuplicatesSet;
  };
  const isAddMoreButtonDisabled = () => {
    // Check if main segment field has errors (special chars, max length)
    const hasMainSegmentError = segmentError && segmentError.trim().length > 0;
    if (hasMainSegmentError) return true;

    // Check if there are any validation errors for current fields only
    // First get current field keys
    const currentFieldKeys = Object.keys(subsegmentValues);

    // Then check if any current field has an error
    const hasAnyValidationError = Object.keys(subsegmentEditError).some(
      errorKey => currentFieldKeys.includes(errorKey)
    );

    if (hasAnyValidationError) return true;

    // Get normalized subsegment values
    const normalizedValues = Object.values(subsegmentValues)
      .filter(Boolean)
      .map(v => v.trim().toLowerCase());

    // Handle edit mode
    if (!isAddNewBox) {
      if (newSubsegments.length === 0) return false;

      const lastNewKey = newSubsegments[newSubsegments.length - 1];
      const lastValue = lastNewKey
        ? subsegmentValues[lastNewKey]?.trim()
        : null;

      // Disable if last value is empty
      if (!lastValue) return true;

      // Check for duplicates within current form values
      if (checkForDuplicateValues(normalizedValues)) return true;

      // Check if the value would conflict with existing API data (using same logic as validation)
      const duplicateError = validateSubsegmentForDuplicates(
        lastNewKey,
        lastValue,
        subsegmentValues
      );
      return duplicateError !== null;
    }

    // Handle add mode
    const lastKey =
      newSubsegments.length > 0
        ? newSubsegments[newSubsegments.length - 1]
        : 'new-0';

    const lastValue = subsegmentValues[lastKey]?.trim();

    // Disable if last value is empty
    if (!lastValue) return true;

    // Check for duplicates within current form values
    if (checkForDuplicateValues(normalizedValues)) return true;

    // Check if the value would conflict with existing API data (using same logic as validation)
    const duplicateError = validateSubsegmentForDuplicates(
      lastKey,
      lastValue,
      subsegmentValues
    );
    return duplicateError !== null;
  };

  return (
    <CustomDialog
      onClose={cancel}
      open={open}
      className="taxonomy-edit-box-wrapper"
    >
      <DialogHeader
        onClose={cancel}
        title={
          <span className="taxonomy-editbox-header-title">{`${dialogTitlePrefix} ${selectedTaxonomyType}`}</span>
        }
      />
      <DialogContent>
        <div className="taxonomy-edit__container">
          {selectedTaxonomyType !== 'segment' && (
            <div className="taxonomy-edit__textbox">
              <TextField
                label={
                  mainFieldFocus
                    ? `${formatTaxonomyText(selectedTaxonomyType)} Name`
                    : ''
                }
                value={textBoxValue}
                onChange={handleTextBoxValueChange}
                onFocus={() => setMainFieldFocus(true)}
                onBlur={() => setMainFieldFocus(false)}
                margin="dense"
                size="small"
                fullWidth
                autoComplete="off"
                className="segment-textfield"
                placeholder={
                  !mainFieldFocus && !textBoxValue
                    ? `${constants.TAXONOMY_PLACEHOLDER_PREFIX} ${selectedTaxonomyType} name`
                    : ''
                }
              />
              <span className="segment-error-message">{taxonomyError}</span>
            </div>
          )}

          {selectedTaxonomyType === 'segment' && (
            <div className="taxonomy-edit__textbox">
              {' '}
              {/* Main TextField for Segment Name */}
              <div className="segment-textfield-container">
                <TextField
                  label={constants.SEGMENT_LABEL}
                  value={textBoxValue}
                  onChange={handleTextBoxValueChange}
                  margin="dense"
                  size="small"
                  fullWidth
                  autoComplete="off"
                  className="segment-textfield segment-placeholder"
                  InputLabelProps={{
                    className:
                      !textBoxValue && segmentError
                        ? 'textfield-label-error'
                        : 'segment-label-color',
                    shrink: true
                  }}
                  InputProps={{
                    className: 'segment-input-color'
                  }}
                  error={isAddNewBox && !!segmentError && !textBoxValue}
                  placeholder={
                    !textBoxValue
                      ? `${constants.TAXONOMY_PLACEHOLDER_PREFIX} ${selectedTaxonomyType} name`
                      : ''
                  }
                />
                <span className="segment-error-message">{segmentError}</span>
              </div>
              {/* Render Subsegment TextFields for Edit Mode */}
              {!isAddNewBox &&
                taxonomies &&
                taxonomies.length > 0 &&
                currentValue !== '' && (
                  <>
                    {taxonomies
                      .filter(taxonomy => taxonomy.value === currentValue)
                      .flatMap(taxonomy => taxonomy.subsegment)
                      .filter(subsegment => subsegment)
                      .map(subsegment => (
                        <div
                          key={subsegment.value}
                          className="taxonomy-subsegment"
                        >
                          <div className="taxonomy-subsegment-child">
                            <TextField
                              label={constants.SUB_SEGMENT_LABEL}
                              value={
                                subsegmentValues[subsegment.value] !== undefined
                                  ? subsegmentValues[subsegment.value]
                                  : subsegment.label
                              }
                              onChange={e =>
                                handleSubsegmentChange(e, subsegment.value)
                              }
                              onFocus={() =>
                                handleSubsegmentFocus(subsegment.value)
                              }
                              onBlur={() =>
                                handleSubsegmentBlur(subsegment.value)
                              }
                              margin="dense"
                              size="small"
                              fullWidth
                              autoComplete="off"
                              className="subsegment-textfield subsegment-label-color subsegment-input-color"
                              InputLabelProps={{
                                className: 'subsegment-label-color'
                              }}
                              InputProps={{
                                className: 'subsegment-input-color'
                              }}
                              placeholder={`${constants.TAXONOMY_PLACEHOLDER_PREFIX} ${constants.SUB_SEGMENT_LABEL.toLowerCase()}`}
                              // error={!!subsegmentEditError[subsegment.value]}
                              // helperText={subsegmentEditError[subsegment.value] || ''}
                            />
                            {!!subsegmentEditError[subsegment.value] && (
                              <span className="sub-segment-error-message">
                                {subsegmentEditError[subsegment.value]}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                  </>
                )}
              {/* Render Subsegment Field Below Segment Field in Add Segment Mode */}{' '}
              {isAddNewBox && (
                <>
                  {' '}
                  <div className="taxonomy-subsegment">
                    <div className="subsegment-input-container">
                      <TextField
                        label={
                          subsegmentFocus['new-0']
                            ? constants.SUB_SEGMENT_LABEL
                            : ''
                        }
                        value={subsegmentValues['new-0'] || ''}
                        onChange={e => handleSubsegmentChange(e, 'new-0')}
                        onFocus={() => {
                          setSubsegmentFocus(prev => ({
                            ...prev,
                            'new-0': true
                          }));
                          handleSubsegmentFocus('new-0');
                        }}
                        onBlur={() => {
                          setSubsegmentFocus(prev => ({
                            ...prev,
                            'new-0': false
                          }));
                          handleSubsegmentBlur('new-0');
                        }}
                        margin="dense"
                        size="small"
                        fullWidth
                        autoComplete="off"
                        className="subsegment-textfield subsegment-label-color subsegment-input-color"
                        InputLabelProps={{
                          className: 'subsegment-label-color'
                        }}
                        InputProps={{ className: 'subsegment-input-color' }}
                        placeholder={
                          !subsegmentFocus['new-0']
                            ? `${constants.TAXONOMY_PLACEHOLDER_PREFIX} ${constants.SUB_SEGMENT_LABEL.toLowerCase()}`
                            : ''
                        }
                      />
                      {!!subsegmentEditError['new-0'] && (
                        <span className="sub-segment-error-message">
                          {subsegmentEditError['new-0']}
                        </span>
                      )}
                    </div>
                  </div>{' '}
                  {newSubsegments.map(key => (
                    <div key={key} className="taxonomy-subsegment">
                      <div className="text-field-icon-container">
                        <TextField
                          label={
                            subsegmentFocus[key]
                              ? constants.SUB_SEGMENT_LABEL
                              : ''
                          }
                          value={subsegmentValues[key] || ''}
                          onChange={e => handleSubsegmentChange(e, key)}
                          onFocus={() => {
                            setSubsegmentFocus(prev => ({
                              ...prev,
                              [key]: true
                            }));
                            handleSubsegmentFocus(key);
                          }}
                          onBlur={() => {
                            setSubsegmentFocus(prev => ({
                              ...prev,
                              [key]: false
                            }));
                            handleSubsegmentBlur(key);
                          }}
                          margin="dense"
                          size="small"
                          fullWidth
                          autoComplete="off"
                          className="subsegment-textfield subsegment-label-color subsegment-input-color"
                          InputLabelProps={{
                            className: 'subsegment-label-color'
                          }}
                          InputProps={{ className: 'subsegment-input-color' }}
                          placeholder={
                            !subsegmentFocus[key]
                              ? `${constants.TAXONOMY_PLACEHOLDER_PREFIX} ${constants.SUB_SEGMENT_LABEL.toLowerCase()}`
                              : ''
                          }
                        />
                        <IconButton
                          onClick={() => handleDeleteSubsegmentClick(key)}
                          className="subsegment-icon-button"
                        >
                          <CancelOutlinedIcon className="subsegment-cancel-button" />
                        </IconButton>
                      </div>
                      {!!subsegmentEditError[key] && (
                        <span className="sub-segment-error-message">
                          {subsegmentEditError[key]}
                        </span>
                      )}
                    </div>
                  ))}
                </>
              )}{' '}
              {!isAddNewBox && newSubsegments && newSubsegments.length > 0 && (
                <>
                  {newSubsegments.map(key => (
                    <div key={key} className="taxonomy-subsegment">
                      <div className="text-field-icon-container">
                        <TextField
                          label={
                            subsegmentFocus[key]
                              ? constants.SUB_SEGMENT_LABEL
                              : ''
                          }
                          value={subsegmentValues[key] || ''}
                          onChange={e => handleSubsegmentChange(e, key)}
                          onFocus={() => {
                            setSubsegmentFocus(prev => ({
                              ...prev,
                              [key]: true
                            }));
                            handleSubsegmentFocus(key);
                          }}
                          onBlur={() => {
                            setSubsegmentFocus(prev => ({
                              ...prev,
                              [key]: false
                            }));
                            handleSubsegmentBlur(key);
                          }}
                          margin="dense"
                          size="small"
                          fullWidth
                          autoComplete="off"
                          className="subsegment-textfield subsegment-label-color subsegment-input-color"
                          InputLabelProps={{
                            className: 'subsegment-label-color'
                          }}
                          InputProps={{ className: 'subsegment-input-color' }}
                          placeholder={
                            !subsegmentFocus[key]
                              ? `${constants.TAXONOMY_PLACEHOLDER_PREFIX} ${constants.SUB_SEGMENT_LABEL.toLowerCase()}`
                              : ''
                          }
                        />
                        <IconButton
                          onClick={() => handleDeleteSubsegmentClick(key)}
                          className="subsegment-icon-button"
                        >
                          <CancelOutlinedIcon className="subsegment-cancel-button" />
                        </IconButton>
                      </div>
                      {!!subsegmentEditError[key] && (
                        <span className="sub-segment-error-message">
                          {subsegmentEditError[key]}
                        </span>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
        {selectedTaxonomyType === 'segment' && (
          <Button
            className="add-more-segment-icon"
            onClick={handleAddMoreSubsegment}
            disabled={isAddMoreButtonDisabled()}
          >
            <AddIcon fontSize="small" />
            <Typography className="add-more-segment">
              {constants.ADD_MORE_SUBSEGMENT}
            </Typography>
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          className="btnSave btn-save-color"
          data-testid="btn-save"
          disabled={!isSaveEnabled}
          onClick={handleSave}
          aria-label={isAddNewBox ? 'Add taxonomy' : 'Save taxonomy'}
        >
          <SaveOutlinedIcon className="btn-save-color-icon" fontSize="medium" />
          Save
        </Button>
      </DialogActions>
      {/* Confirmation Dialog for Subsegment Delete */}
      <CustomDialog
        open={deleteDialogOpen}
        onClose={handleCancelDeleteSubsegment}
        className="subsegment-delete-dialog"
      >
        <DialogHeader
          onClose={handleCancelDeleteSubsegment}
          title={
            <div className="subsegment-delete-dialog-header-text">
              <ErrorOutlineIcon className="subsegment-delete-dialog-header-icon" />
              {constants.DELETE_SUBSEGMENT_HEADER}
            </div>
          }
        />
        <DialogContent>
          <Typography
            className="subsegment-delete-dialog-text-typo"
            style={{ marginLeft: '24px' }}
          >
            {`${constants.TAXONOMY_DELETE} ${getDeleteSubsegmentText()} sub segment?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelDeleteSubsegment}
            color="primary"
            variant="outlined"
            className="taxonomy-action-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteSubsegment}
            color="error"
            variant="contained"
            startIcon={<DeleteOutlineOutlinedIcon />}
            className="taxonomy-action-button"
          >
            Delete
          </Button>
        </DialogActions>
      </CustomDialog>
    </CustomDialog>
  );
};

ContentMetadataEditBox.propTypes = {
  open: PropTypes.bool.isRequired,
  closeEditBox: PropTypes.func.isRequired,
  handleAddTaxonomy: PropTypes.func.isRequired,
  handleEditTaxonomy: PropTypes.func.isRequired,
  isAddNewBox: PropTypes.bool.isRequired,
  currentValue: PropTypes.string,
  currentLabel: PropTypes.string, // <-- add this line
  selectedTaxonomyType: PropTypes.string.isRequired,
  taxonomies: PropTypes.array
};

export default ContentMetadataEditBox;
